import { useState, useEffect, useMemo } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import './CapitalScreen.css';
import Loader from '../../components/Common/Loader';
import MobileCapital from './MobileCapital';
import { useScreenType } from '../../hooks/useScreenType';
import usePageTitle from '../../hooks/usePageTitle';

// components
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import TopBar from '../../components/Tables/TopBar/TopBar';
import CapitalRow from '../../components/Tables/Capital/CapitalRow';
import CapitalFilters, {
  CapitalFiltersState,
  Period,
  PriceRange,
  VariationFilter,
  AmountRange,
} from './CapitalFilters';

import DesktopCapital from './DesktopCapital';

// interfaces
interface IStock {
  id: number;
  name: string;
  symbol: string;
  amount: string;
  change: string;
  status: number;
  currency: string;
  lineChartData: number[];
  variation_pct: number;
  sector?: string;
  volume_montant?: number;
}

// ... existing helpers ...
// Helper to map new period values to API values
const mapPeriodToApi = (period: Period): string => {
  switch (period) {
    case 'day': return 'default';
    case 'last_5': return 'last_5';
    case 'last_week': return 'last_week';
    case 'last_month': return 'last_month';
    case 'last_3_months': return 'last_3_months';
    case 'last_6_months': return 'last_6_months';
    case 'ytd': return 'ytd';
    case 'last_12_months': return 'last_12_months';
    default: return 'default';
  }
};

const parsePrice = (amount: string): number => {
  if (!amount) return 0;
  return parseFloat(amount.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
};

const matchesPriceRange = (price: number, range: PriceRange): boolean => {
  switch (range) {
    case 'all': return true;
    case 'under_2000': return price < 2000;
    case '2000_10000': return price >= 2000 && price < 10000;
    case '10000_50000': return price >= 10000 && price < 50000;
    case 'over_50000': return price >= 50000;
    default: return true;
  }
};

const matchesVariation = (variationPct: number, filter: VariationFilter): boolean => {
  switch (filter) {
    case 'all': return true;
    case 'up': return variationPct > 0;
    case 'down': return variationPct < 0;
    case 'unchanged': return variationPct === 0;
    default: return true;
  }
};

const matchesAmountRange = (amount: number, range: AmountRange): boolean => {
  if (!amount) amount = 0;
  switch (range) {
    case 'all': return true;
    case 'under_20m': return amount < 20000000;
    case '20m_50m': return amount >= 20000000 && amount < 50000000;
    case '50m_100m': return amount >= 50000000 && amount < 100000000;
    case 'over_100m': return amount >= 100000000;
    default: return true;
  }
};

const CapitalScreen: React.FC = () => {
  const [data, setData] = useState<IStock[]>([]);
  const [keyword, setKeyword] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [filters, setFilters] = useState<CapitalFiltersState>({
    period: 'day',
    priceRange: 'all',
    variation: 'all',
    amountRange: 'all',
    sector: 'all',
    favoritesOnly: false,
  });

  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const { token } = useAuth();
  const { isMobile } = useScreenType();
  
  usePageTitle('Marché des actions (Capital)');

  const sectors = useMemo(() => {
    const sectorSet = new Set<string>();
    data.forEach((stock) => {
      if (stock.sector) {
        const mainSector = stock.sector.split('/')[0].trim();
        if (mainSector) sectorSet.add(mainSector);
      }
    });
    return Array.from(sectorSet).sort();
  }, [data]);

  useEffect(() => {
    if (token) fetchStockData();
  }, [filters.period, token]);

  useEffect(() => {
    if (token) fetchFavorites();
  }, [token]);

  const fetchStockData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const apiPeriod = mapPeriodToApi(filters.period);
      const response = await fetch(`${API_URL}/v1/market/capital?period=${apiPeriod}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const stocks = await response.json();
        setData(Array.isArray(stocks) ? stocks : []);
      }
    } catch (error) {
      console.error('Error fetching capital data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await fetch(`${API_URL}/api/favorites`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setFavorites(new Set<string>(data.map((item: any) => item.symbol)));
      }
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  const handleToggleFavorite = (symbol: string, isFav: boolean) => {
    const newFavorites = new Set(favorites);
    if (isFav) newFavorites.add(symbol);
    else newFavorites.delete(symbol);
    setFavorites(newFavorites);
  };

  const filteredData = useMemo(() => {
    return data.filter((stock) => {
      const matchesKeyword = !keyword || 
        (stock.name && stock.name.toLowerCase().includes(keyword.toLowerCase())) ||
        (stock.symbol && stock.symbol.toLowerCase().includes(keyword.toLowerCase()));
      const price = parsePrice(stock.amount);
      const matchesPrice = matchesPriceRange(price, filters.priceRange);
      const matchesVar = matchesVariation(stock.variation_pct || 0, filters.variation);
      const stockMainSector = stock.sector ? stock.sector.split('/')[0].trim() : undefined;
      const matchesSector = filters.sector === 'all' || stockMainSector === filters.sector;
      const matchesAmount = matchesAmountRange(stock.volume_montant || 0, filters.amountRange);
      const matchesFavorites = !filters.favoritesOnly || favorites.has(stock.symbol);
      return matchesKeyword && matchesPrice && matchesVar && matchesSector && matchesAmount && matchesFavorites;
    });
  }, [data, keyword, filters, favorites]);

  const getVariationLabel = () => {
    switch (filters.period) {
      case 'day': return 'Jour';
      case 'last_5': return '5J';
      case 'last_week': return 'Semaine';
      case 'last_month': return 'Mois';
      case 'last_3_months': return '3 Mois';
      case 'last_6_months': return '6 Mois';
      case 'ytd': return 'YTD';
      case 'last_12_months': return '12 Mois';
      default: return 'Jour';
    }
  };

  const capitalProps = {
    data,
    filteredData,
    keyword,
    setKeyword,
    filters,
    setFilters,
    favorites,
    onToggleFavorite: handleToggleFavorite,
    sectors,
    getVariationLabel
  };

  return (
    <SiteLayout>
      <Header icon='show_chart' title='Marché des actions' />
      {loading ? (
        <Loader message="Chargement des cotations..." />
      ) : isMobile ? (
        <MobileCapital 
          {...capitalProps} 
          data={filteredData} // Mobile expects 'data' to be the filtered list
          totalCount={data.length} // Pass total count explicitly
        />
      ) : (
        <DesktopCapital {...capitalProps} />
      )}
    </SiteLayout>
  );
};

export default CapitalScreen;

